import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import useAuthStore from "../store/authStore";
import OtpVerify from "../components/OtpVerify";
import { Package, Eye, EyeOff, Loader2, ArrowRight, Mail, Lock, User, Shield, Phone, MapPin, Truck, CreditCard, Gift, CheckCircle2 } from "lucide-react";
import GoogleSignInButton from "../components/GoogleSignInButton";
import Logo from "../components/Logo";
import api from "../services/api";
import { toast } from "sonner";

export default function Register() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [vehicleType, setVehicleType] = useState("bike");
  const [idProof, setIdProof] = useState("");
  const [role, setRole] = useState(() => searchParams.get("role") === "agent" ? "agent" : "user");
  const [referralCode, setReferralCode] = useState(() => searchParams.get("ref") || "");
  const [referralValid, setReferralValid] = useState(null);
  const [referralName, setReferralName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { register } = useAuthStore();
  const [otpStep, setOtpStep] = useState(null);   // Sprint-87 — OTP screen state
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const fieldError = (field, value) => touched[field] && !value.trim();
  const errorBorder = (field, value) => fieldError(field, value) ? "border-red-400 ring-2 ring-red-100" : "border-slate-200";

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  // Validate referral code
  useEffect(() => {
    const code = referralCode.trim();
    if (code.length < 4) { setReferralValid(null); setReferralName(""); return; }
    const timer = setTimeout(() => {
      api.get(`/referral/validate/${code}`)
        .then(({ data }) => { setReferralValid(data.valid); setReferralName(data.referrer_name || ""); })
        .catch(() => setReferralValid(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true, phone: true, address: true, idProof: true });
    const errors = [];
    if (!name.trim()) errors.push("Full Name");
    if (!email.trim() || !email.includes("@")) errors.push("Valid Email");
    if (password.length < 6) errors.push("Password (min 6 chars)");
    if (!phone.trim() || phone.trim().length < 10) errors.push("Phone Number");
    if (!address.trim()) errors.push("Address");
    if (role === "agent" && !idProof.trim()) errors.push("ID Proof Number");
    if (errors.length > 0) {
      toast.error(`Please fill: ${errors.join(", ")}`);
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const res = await register(name, email, password, role, phone || undefined, address || undefined, role === "agent" ? { vehicle_type: vehicleType, id_proof: idProof } : undefined, referralCode.trim() || undefined);
      // Sprint-87 — registration is OTP-gated. If the backend says so,
      // transition to the verification screen instead of routing.
      if (res && res.otp_required) {
        setOtpStep({ registrationToken: res.registration_token, emailMasked: res.email_masked, resendAfter: res.resend_after_s || 60 });
        toast.message(`Verification code sent to ${res.email_masked}`);
        return;
      }
      if (role === "agent") {
        toast.success("Agent registration submitted! Please wait for admin approval before you can accept orders.");
      } else {
        toast.success("Account created! Welcome to PickNGo.");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Sprint-87 — render the OTP step in-place over the register form.
  if (otpStep) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="register-otp-step">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 w-full max-w-md">
          <OtpVerify
            loginToken={otpStep.registrationToken}
            emailMasked={otpStep.emailMasked}
            resendAfter={otpStep.resendAfter}
            purpose="registration"
            onSuccess={() => navigate("/dashboard")}
            onCancel={() => setOtpStep(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0B132B] relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/15 to-transparent pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-[#FF6B00]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <Logo size="lg" textColor="text-white" />
          </div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white leading-tight mb-4">
            Start delivering<br/>with PickNGo today
          </h2>
          <p className="text-slate-400 text-base leading-relaxed">
            Whether you're sending packages or delivering them, PickNGo makes logistics simple, fast, and reliable.
          </p>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Shield className="h-4 w-4 text-green-400" />
              </div>
              <span className="text-sm text-slate-300">{stats ? `${stats.deliveries_completed}+` : "..."} deliveries completed</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Package className="h-4 w-4 text-orange-400" />
              </div>
              <span className="text-sm text-slate-300">{stats ? `${stats.on_time_rate}%` : "..."} on-time delivery rate</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <User className="h-4 w-4 text-blue-400" />
              </div>
              <span className="text-sm text-slate-300">{stats ? `${stats.active_agents}+` : "..."} verified delivery agents</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-fadeInUp">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <Logo size="md" />
          </div>

          <div className="mb-8">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Create your account</h1>
            <p className="text-sm text-slate-500 mt-2">Join PickNGo and start shipping in minutes</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`rounded-xl ${errorBorder("name", name)} h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20`}
                  required
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  data-testid="register-name-input"
                />
                {fieldError("name", name) && <p className="text-[10px] text-red-500 mt-1">Full name is required</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`rounded-xl ${errorBorder("email", email)} h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20`}
                  required
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  data-testid="register-email-input"
                />
                {fieldError("email", email) && <p className="text-[10px] text-red-500 mt-1">Valid email is required</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`rounded-xl ${touched.password && password.length < 6 ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"} h-12 pl-10 pr-12 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20`}
                  required
                  onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                {touched.password && password.length < 6 && <p className="text-[10px] text-red-500 mt-1 absolute -bottom-4 left-0">Min 6 characters</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`rounded-xl ${errorBorder("phone", phone)} h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20`}
                  required
                  onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                  data-testid="register-phone-input"
                />
                {fieldError("phone", phone) && <p className="text-[10px] text-red-500 mt-1">Phone number is required</p>}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Address</Label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Your city or full address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className={`rounded-xl ${errorBorder("address", address)} h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20`}
                  required
                  onBlur={() => setTouched((t) => ({ ...t, address: true }))}
                  data-testid="register-address-input"
                />
                {fieldError("address", address) && <p className="text-[10px] text-red-500 mt-1">Address is required</p>}
              </div>
            </div>
            {searchParams.get("role") === "agent" && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">I want to</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="rounded-xl border-slate-200 h-12" data-testid="register-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="user">Send Packages (Customer)</SelectItem>
                  <SelectItem value="agent">Deliver Packages (Agent)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {role === "agent" && (
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-orange-800">
                  <strong>Agent Registration:</strong> After signing up, your account needs admin approval before you can start accepting orders.
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Vehicle Type</Label>
                  <Select value={vehicleType} onValueChange={setVehicleType}>
                    <SelectTrigger className="rounded-xl border-slate-200 h-12" data-testid="register-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="bike">Bike</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="truck">Truck</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">ID Proof Number (Aadhaar / License)</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Enter your ID number"
                      value={idProof}
                      onChange={(e) => setIdProof(e.target.value)}
                      className={`rounded-xl ${errorBorder("idProof", idProof)} h-12 pl-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20`}
                      required
                      onBlur={() => setTouched((t) => ({ ...t, idProof: true }))}
                      data-testid="register-idproof-input"
                    />
                    {fieldError("idProof", idProof) && <p className="text-[10px] text-red-500 mt-1">ID proof number is required</p>}
                  </div>
                </div>
              </>
            )}

            {/* Referral Code */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Referral Code <span className="text-slate-400 text-xs">(optional)</span></Label>
              <div className="relative">
                <Gift className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Enter referral code"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  className={`rounded-xl h-12 pl-10 pr-10 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 ${referralValid === true ? "border-green-400 ring-2 ring-green-100" : referralValid === false ? "border-red-400 ring-2 ring-red-100" : "border-slate-200"}`}
                  data-testid="register-referral-input"
                />
                {referralValid === true && <CheckCircle2 className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
              </div>
              {referralValid === true && referralName && (
                <p className="text-xs text-green-600 font-medium">Referred by {referralName} — you'll get INR 30 off your first order!</p>
              )}
              {referralValid === false && referralCode.trim().length >= 4 && (
                <p className="text-xs text-red-500">Invalid referral code</p>
              )}
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer pt-1" data-testid="register-terms-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#FF6B00] focus:ring-[#FF6B00]/30 cursor-pointer"
                data-testid="register-terms-checkbox"
              />
              <span className="text-xs text-slate-600 leading-relaxed">
                I agree to PickNGo's{" "}
                <Link to="/terms" target="_blank" className="text-[#FF6B00] font-medium hover:underline" data-testid="register-terms-link">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" target="_blank" className="text-[#FF6B00] font-medium hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || !acceptTerms}
              className="w-full bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl h-12 font-semibold shadow-sm shadow-[#FF6B00]/20 hover:shadow-md transition-all active:scale-[0.98] mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <ArrowRight className="h-5 w-5 mr-2" />}
              {loading ? "Creating account..." : "Create Account"}
            </Button>

            <GoogleSignInButton redirectTo="/dashboard" />
          </form>

          <p className="text-sm text-center text-slate-500 mt-8" data-testid="register-login-link">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF6B00] font-semibold hover:text-[#E65A00] transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
